// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/** @type {HTMLElement[]} */
const focusTrapStack = [];

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((element) => element instanceof HTMLElement && element.getClientRects().length > 0);
}

/** @param {HTMLElement} container */
function isActiveTrapContainer(container) {
  if (!container.isConnected) return false;
  if (container instanceof HTMLDialogElement) return container.open;
  if (container instanceof HTMLDetailsElement) return container.open;
  return true;
}

function pruneFocusTrapStack() {
  for (let index = focusTrapStack.length - 1; index >= 0; index -= 1) {
    if (!isActiveTrapContainer(focusTrapStack[index])) focusTrapStack.splice(index, 1);
  }
}

function detachFocusTrapHandlers() {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
  delete trapFocusHandlers.keydown;
  delete trapFocusHandlers.focusin;
}

/** @param {boolean} [moveFocus] */
function activateTopFocusTrap(moveFocus = false) {
  detachFocusTrapHandlers();
  pruneFocusTrapStack();

  const container = focusTrapStack[focusTrapStack.length - 1];
  if (!container) return;

  trapFocusHandlers.keydown = (event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Tab') return;

    const focusable = getFocusableElements(container);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement;

    if (!first || !last) {
      event.preventDefault();
      container.focus();
      return;
    }

    if (!event.shiftKey && (activeElement === last || !container.contains(activeElement))) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && (activeElement === first || activeElement === container || !container.contains(activeElement))) {
      event.preventDefault();
      last.focus();
    }
  };

  trapFocusHandlers.focusin = (event) => {
    if (event.target instanceof Node && !container.contains(event.target)) {
      event.stopPropagation();
      (getFocusableElements(container)[0] ?? container).focus();
    }
  };

  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  if (moveFocus && !container.contains(document.activeElement)) {
    (getFocusableElements(container)[0] ?? container).focus();
  }
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 */
export function trapFocus(container) {
  pruneFocusTrapStack();

  const existingIndex = focusTrapStack.lastIndexOf(container);
  if (existingIndex >= 0) focusTrapStack.splice(existingIndex, 1);
  focusTrapStack.push(container);

  activateTopFocusTrap(true);
}

/**
 * Remove a focus trap. If another active trap remains underneath it, that
 * trap is reactivated without moving focus.
 * @param {HTMLElement} [container] - Specific trap to remove; defaults to the top trap.
 */
export function removeTrapFocus(container) {
  pruneFocusTrapStack();

  if (container) {
    const index = focusTrapStack.lastIndexOf(container);
    if (index >= 0) focusTrapStack.splice(index, 1);
  } else {
    focusTrapStack.pop();
  }

  activateTopFocusTrap();
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
